/**
 * Settings → Design systems, in the shape of the Skills tab: one row a
 * design system, Edit opening a dialog with its name, its tokens (CSS custom
 * properties, or a tokens JSON pasted, kept as CSS) and its guidance, and
 * Delete. A project picks one for its chats, and a chat may pick its own
 * (DesignControls.tsx). An artifact's look is saved as one from its panel.
 */

import { useEffect, useState } from 'react';
import {
  ApiError,
  createDesignSystem,
  deleteDesignSystem,
  listDesignSystems,
  patchDesignSystem,
  type DesignSystem
} from '../../api';
import { ds } from '../../ds';
import { Ph } from '../ui';

const FAILED = 'That did not go through. Try again.';

/** The first few colours its tokens set, for the row's swatches. */
export function swatches(tokens: string): string[] {
  const out: string[] = [];
  for (const m of tokens.matchAll(/--[\w-]+:\s*(#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|oklch\([^)]*\))\s*;/g)) {
    if (!out.includes(m[1]!)) out.push(m[1]!);
    if (out.length === 5) break;
  }
  return out;
}

export function SystemDialog({
  system,
  draft,
  onSaved,
  onDeleted,
  onClose
}: {
  system: DesignSystem | null;
  /** A new system's starting fields, such as a look read from an artifact,
   *  shown for review and editing before anything is kept. */
  draft?: { name: string; tokens: string; guidance: string };
  onSaved: (ds: DesignSystem) => void;
  onDeleted: (id: string) => void;
  onClose: () => void;
}) {
  const { Dialog, Button, Input } = ds();
  const [name, setName] = useState(system?.name ?? draft?.name ?? '');
  const [tokens, setTokens] = useState(system?.tokens ?? draft?.tokens ?? '');
  const [guidance, setGuidance] = useState(system?.guidance ?? draft?.guidance ?? '');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<{ field: string | null; message: string } | null>(null);

  const fail = (err: unknown) => {
    const field = err instanceof ApiError && typeof err.details.field === 'string' ? err.details.field : null;
    setError({ field, message: err instanceof ApiError && err.status !== 0 ? err.message : FAILED });
    setConfirming(false);
    setBusy(false);
  };

  const save = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const fields = { name: name.trim(), tokens, guidance: guidance.trim() };
    (system ? patchDesignSystem(system.id, fields) : createDesignSystem(fields)).then((saved) => {
      onSaved(saved);
      onClose();
    }, fail);
  };

  const remove = () => {
    if (!system || busy) return;
    setBusy(true);
    deleteDesignSystem(system.id).then(() => {
      onDeleted(system.id);
      onClose();
    }, fail);
  };

  const fieldError = (field: string) => (error?.field === field ? error.message : undefined);

  return (
    <Dialog
      open
      position="fixed"
      width={600}
      title={system ? system.name : 'New design system'}
      onClose={onClose}
      style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 16, maxHeight: '100%', overflowY: 'auto' }}
      footer={
        confirming && system ? (
          <>
            <span style={{ flex: 1, alignSelf: 'center', fontSize: 13.5, color: 'var(--text-body)' }}>Delete {system.name}?</span>
            <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="danger" density="app" size="sm" disabled={busy} onClick={remove}>
              Delete
            </Button>
          </>
        ) : (
          <>
            {system ? (
              <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={() => setConfirming(true)}>
                Delete
              </Button>
            ) : null}
            <span style={{ flex: 1 }} />
            <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" density="app" size="sm" disabled={busy || !name.trim() || !tokens.trim()} onClick={save}>
              {system ? 'Save' : 'Add'}
            </Button>
          </>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} error={fieldError('name')} autoFocus={!system} />
        <Input
          label="Tokens"
          multiline
          rows={10}
          placeholder={':root {\n  --color-primary: #663399;\n  --radius-card: 12px;\n}'}
          hint="CSS custom properties, or a tokens JSON."
          value={tokens}
          onChange={(e) => setTokens(e.target.value)}
          error={fieldError('tokens')}
          style={{ fontFamily: 'var(--font-mono)' }}
        />
        <Input label="Guidance" multiline rows={6} value={guidance} onChange={(e) => setGuidance(e.target.value)} error={fieldError('guidance')} />
        {error && !error.field ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{error.message}</span> : null}
      </div>
    </Dialog>
  );
}

export function DesignTab() {
  const { Button } = ds();
  const [systems, setSystems] = useState<DesignSystem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<DesignSystem | 'new' | null>(null);

  useEffect(() => {
    listDesignSystems().then(
      (r) => setSystems(r.designSystems),
      () => setFailed(true)
    );
  }, []);

  if (failed) return <span style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>Design systems did not load.</span>;
  if (!systems) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {systems.length ? (
        <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
          {systems.map((s) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 58, padding: '0 16px', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ display: 'flex', flex: 'none', width: 32, justifyContent: 'center' }}>
                {swatches(s.tokens).length ? (
                  swatches(s.tokens).slice(0, 3).map((c) => (
                    <span key={c} style={{ width: 12, height: 24, marginLeft: -3, borderRadius: 4, background: c, border: '1px solid var(--border-subtle)' }} />
                  ))
                ) : (
                  <Ph name="palette" size={16} color="var(--text-muted)" />
                )}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {s.name}
              </span>
              <button type="button" className="bare hov-strong" onClick={() => setEditing(s)} style={{ fontSize: 13, color: 'var(--text-link)' }}>
                Edit
              </button>
            </div>
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>
          No design systems yet. Add one, or save an artifact’s look from its panel.
        </span>
      )}
      <div>
        <Button variant="accent" density="app" size="sm" iconLeft="plus" onClick={() => setEditing('new')}>
          Add a design system
        </Button>
      </div>
      {editing ? (
        <SystemDialog
          system={editing === 'new' ? null : editing}
          onSaved={(saved) => setSystems((list) => (list?.some((x) => x.id === saved.id) ? list.map((x) => (x.id === saved.id ? saved : x)) : [...(list ?? []), saved]))}
          onDeleted={(id) => setSystems((list) => list?.filter((x) => x.id !== id) ?? null)}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
